import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, ArrowLeft, Eye, EyeOff, User, Phone, Loader2, Gift, FileText, CheckCircle2, X, Sparkles } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.97 10.97 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

export default function LoginSoci() {
  const [showPwd, setShowPwd] = useState(false);
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", nome: "", cognome: "", telefono: "" });
  // Guest registration modal state
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestStep, setGuestStep] = useState(1); // 1 anagrafica, 2 statuto, 3 success
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState("");
  const [guestForm, setGuestForm] = useState({ email: "", nome: "", cognome: "", telefono: "", accetto_statuto: false, accetto_privacy: false });
  const navigate = useNavigate();

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openGuestWizard = (prefillEmail = "") => {
    setGuestForm({ email: prefillEmail, nome: "", cognome: "", telefono: "", accetto_statuto: false, accetto_privacy: false });
    setGuestStep(1);
    setGuestError("");
    setGuestOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = mode === "login" ? `${API}/api/auth/login` : `${API}/api/auth/register`;
      const body = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, nome: form.nome, cognome: form.cognome, telefono: form.telefono };
      const { data } = await axios.post(url, body, { withCredentials: true });
      sessionStorage.setItem("acdt_user", JSON.stringify(data));
      // SIGNUP: redirigi subito a Stripe per pagare la tessera €50 (obbligatorio per attivare l'account).
      // Bypass per admin/direttivo/utenti già con accesso_pieno.
      if (
        mode === "signup" &&
        data.role !== "admin" &&
        !data.ruolo_direttivo &&
        !data.accesso_pieno
      ) {
        try {
          const res = await axios.post(
            `${API}/api/checkout/create-session`,
            {
              package_id: "tessera",
              origin_url: window.location.origin,
              billing: {
                nome: data.nome || form.nome,
                cognome: data.cognome || form.cognome,
                email: data.email || form.email,
                telefono: data.telefono || form.telefono || "",
              },
            },
            { withCredentials: true }
          );
          const checkoutUrl = res.data?.url;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
        } catch (payErr) {
          // Se il checkout fallisce, mostriamo il paywall nella dashboard
          console.error("Checkout auto-init failed:", payErr);
        }
      }
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatError(detail) || err.message;
      setError(msg);
      // Se in modalità login + errore credenziali → verifica se email esiste
      if (mode === "login" && form.email) {
        try {
          const check = await axios.post(`${API}/api/auth/check-email`, { email: form.email });
          if (!check.data.exists) {
            // Email NON registrata → proponi guest wizard
            setError("");
            openGuestWizard(form.email);
          }
        } catch (_) { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const submitGuest = async () => {
    setGuestLoading(true);
    setGuestError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/register-guest`, guestForm, { withCredentials: true });
      sessionStorage.setItem("acdt_user", JSON.stringify({ id: data.user_id, tipo_socio: "guest", role: "socio" }));
      setGuestStep(3);
    } catch (err) {
      setGuestError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#f5f7ff]">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a]">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#f59e0b]/25 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative flex flex-col justify-between p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#fbbf24]">
            <ArrowLeft className="h-4 w-4" /> Torna al sito
          </Link>
          <div>
            <div className="h-40 w-40 md:h-48 md:w-48 xl:h-56 xl:w-56 rounded-3xl bg-white p-3 md:p-4 flex items-center justify-center mb-8 shadow-2xl">
              <img src={LOGO_URL} alt="ACDT" className="h-full w-full object-contain" />
            </div>
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.05]">
              Area Riservata<br />
              <span className="bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] bg-clip-text text-transparent">Soci A.C.D.T.</span>
            </h1>
            <p className="mt-6 text-slate-200 text-lg">
              Gestisci tesseramento, crediti CFDT, attestati e rinnovi. Tutto in un unico posto.
            </p>
            <ul className="mt-8 space-y-3 text-[14px] text-slate-200">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24]" /> Tessera digitale + QR Code</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24]" /> Calcolo automatico CFDT</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24]" /> Rinnovo online in un click</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24]" /> Download attestati e documenti</li>
            </ul>
          </div>
          <p className="text-[12px] text-slate-400">© {new Date().getFullYear()} A.C.D.T. — Tutti i diritti riservati</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm font-semibold text-[#102a6b] mb-6">
            <ArrowLeft className="h-4 w-4" /> Torna al sito
          </Link>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a1f55]">
            {mode === "login" ? "Accedi alla tua area" : "Registrati"}
          </h2>
          <p className="mt-2 text-slate-600">
            {mode === "login" ? "Bentornato! Inserisci le tue credenziali." : "Crea il tuo account socio A.C.D.T."}
          </p>

          <details className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[12.5px]">
            <summary className="cursor-pointer text-slate-600 font-semibold hover:text-[#0a1f55]">
              Sei un membro del Direttivo A.C.D.T.?
            </summary>
            <div className="mt-3 space-y-2">
              <p className="text-[11.5px] text-slate-500 leading-relaxed">
                L&apos;accesso rapido con Google è riservato ai membri del Direttivo e agli amministratori.
                Gli altri soci devono usare email e password.
              </p>
              <button
                type="button"
                data-testid="btn-google-login"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/auth-callback";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-10 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 transition-colors flex items-center justify-center gap-2 font-semibold text-slate-700 text-[13px]"
              >
                <GoogleIcon />
                Accesso Direttivo con Google
              </button>
            </div>
          </details>

          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400 font-bold">
            <div className="flex-1 h-px bg-slate-200" />
            accedi con credenziali
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[13px] font-semibold text-[#0a1f55]">Nome</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="signup-nome" required value={form.nome} onChange={onChange("nome")} className="pl-10 h-11" placeholder="Mario" />
                  </div>
                </div>
                <div>
                  <Label className="text-[13px] font-semibold text-[#0a1f55]">Cognome</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="signup-cognome" required value={form.cognome} onChange={onChange("cognome")} className="pl-10 h-11" placeholder="Rossi" />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-[13px] font-semibold text-[#0a1f55]">
                {mode === "login" ? "Email o N° Tessera" : "Email"}
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="auth-email"
                  type={mode === "login" ? "text" : "email"}
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder={mode === "login" ? "nome@email.it  oppure  ACDT-2026-0001" : "nome@email.it"}
                  className="pl-10 h-11"
                  autoComplete="username"
                />
              </div>
              {mode === "login" && (
                <p className="mt-1 text-[11px] text-slate-500">
                  Puoi accedere con la tua email oppure con il codice tessera (es. ACDT-2026-0001).
                </p>
              )}
            </div>

            {mode === "signup" && (
              <div>
                <Label className="text-[13px] font-semibold text-[#0a1f55]">Telefono <span className="text-slate-400 font-normal">(opzionale)</span></Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="signup-telefono" type="tel" value={form.telefono} onChange={onChange("telefono")} placeholder="+39 333 1234567" className="pl-10 h-11" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-[13px] font-semibold text-[#0a1f55]">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="auth-password" type={showPwd ? "text" : "password"} required minLength={mode === "signup" ? 8 : undefined} value={form.password} onChange={onChange("password")} placeholder="••••••••" className="pl-10 pr-10 h-11" />
                <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#102a6b]">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === "signup" && <p className="mt-1 text-[11px] text-slate-500">Almeno 8 caratteri</p>}
              {mode === "login" && (
                <button
                  type="button"
                  data-testid="btn-forgot-password"
                  onClick={async () => {
                    const em = window.prompt("Inserisci la tua email per ricevere il link di reset:", form.email || "");
                    if (!em) return;
                    try {
                      await axios.post(`${API}/api/auth/forgot-password`, { email: em.trim().toLowerCase() });
                      alert("Se l'email è registrata, ti abbiamo inviato il link per reimpostare la password.");
                    } catch (_) {
                      alert("Se l'email è registrata, ti abbiamo inviato il link per reimpostare la password.");
                    }
                  }}
                  className="mt-2 inline-block text-[12.5px] text-[#102a6b] font-semibold hover:underline"
                >
                  Password dimenticata?
                </button>
              )}
            </div>

            {error && (
              <div data-testid="auth-error" className="p-3 rounded-lg bg-red-50 border border-red-200 text-[12.5px] text-red-700 font-semibold">
                {error}
              </div>
            )}

            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  {mode === "login" ? "Accedi" : "Crea account"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[13.5px] text-slate-600">
            {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }} className="text-[#102a6b] font-bold hover:underline" data-testid="toggle-mode">
              {mode === "login" ? "Registrati ora" : "Accedi"}
            </button>
          </p>

          {/* CTA guest gratuito */}
          <button
            onClick={() => openGuestWizard(form.email)}
            data-testid="open-guest-wizard"
            className="mt-3 w-full p-3 rounded-xl border-2 border-dashed border-[#f59e0b]/40 bg-[#fef3c7]/40 hover:bg-[#fef3c7] transition-colors text-left flex items-center gap-3"
          >
            <Gift className="h-5 w-5 text-[#f59e0b] shrink-0" />
            <div className="flex-1">
              <div className="text-[12.5px] font-extrabold text-[#0a1f55]">Account gratuito + Guida regalo</div>
              <div className="text-[11.5px] text-slate-600">PDF &laquo;Tutelarsi Online in 10 Pillole&raquo; in regalo</div>
            </div>
            <ArrowRight className="h-4 w-4 text-[#0a1f55]" />
          </button>
        </div>
      </div>

      {/* WIZARD GUEST REGISTRATION */}
      {guestOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" data-testid="guest-wizard">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#f59e0b]" />
                <h3 className="font-extrabold text-[#0a1f55] text-lg">
                  {guestStep === 1 && "Crea il tuo account gratuito"}
                  {guestStep === 2 && "Accetta Statuto e Privacy"}
                  {guestStep === 3 && "Account creato! 🎉"}
                </h3>
              </div>
              <button onClick={() => setGuestOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* STEP 1 — Anagrafica */}
              {guestStep === 1 && (
                <div className="space-y-3">
                  <p className="text-[13px] text-slate-600 mb-4">
                    Registrandoti gratuitamente ricevi subito la guida <strong>&laquo;Tutelarsi Online in 10 Pillole&raquo;</strong> + accesso alla dashboard.
                  </p>
                  <div>
                    <Label className="text-[12.5px] font-semibold text-[#0a1f55]">Email</Label>
                    <Input value={guestForm.email} onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })} type="email" placeholder="tu@email.com" className="mt-1 h-11" data-testid="guest-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[12.5px] font-semibold text-[#0a1f55]">Nome</Label>
                      <Input value={guestForm.nome} onChange={(e) => setGuestForm({ ...guestForm, nome: e.target.value })} className="mt-1 h-11" data-testid="guest-nome" />
                    </div>
                    <div>
                      <Label className="text-[12.5px] font-semibold text-[#0a1f55]">Cognome</Label>
                      <Input value={guestForm.cognome} onChange={(e) => setGuestForm({ ...guestForm, cognome: e.target.value })} className="mt-1 h-11" data-testid="guest-cognome" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[12.5px] font-semibold text-[#0a1f55]">Telefono (opzionale)</Label>
                    <Input value={guestForm.telefono} onChange={(e) => setGuestForm({ ...guestForm, telefono: e.target.value })} className="mt-1 h-11" />
                  </div>
                  <Button
                    onClick={() => {
                      if (!guestForm.email || !guestForm.nome || !guestForm.cognome) {
                        setGuestError("Compila email, nome e cognome");
                        return;
                      }
                      setGuestError("");
                      setGuestStep(2);
                    }}
                    className="w-full h-11 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold mt-3"
                    data-testid="guest-next-1"
                  >
                    Continua → <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                  {guestError && <div className="text-red-600 text-[12.5px] font-semibold">{guestError}</div>}
                </div>
              )}

              {/* STEP 2 — Statuto e Privacy */}
              {guestStep === 2 && (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-56 overflow-y-auto text-[12px] text-slate-700 leading-relaxed">
                    <h4 className="font-extrabold text-[#0a1f55] mb-2">Estratto Statuto A.C.D.T.</h4>
                    <p>L&apos;Associazione A.C.D.T. — Associazione Consulenti Digitali Tecnologici — è un&apos;associazione professionale ai sensi della <strong>Legge 4/2013</strong>, aggregata al MIMIT tramite Centro Studi E.S.S.E.</p>
                    <p className="mt-2">Lo Statuto integrale è consultabile alla pagina <Link to="/direttivo-documentazione" className="text-[#102a6b] underline">/direttivo-documentazione</Link>.</p>
                    <p className="mt-2">L&apos;iscrizione come socio comporta l&apos;adesione ai principi etici del Codice Deontologico e al rispetto delle norme di condotta professionale.</p>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={guestForm.accetto_statuto} onChange={(e) => setGuestForm({ ...guestForm, accetto_statuto: e.target.checked })} className="mt-1 h-4 w-4 accent-[#102a6b]" data-testid="guest-statuto" />
                    <span className="text-[12.5px] text-slate-700">Ho letto e accetto lo <strong>Statuto A.C.D.T.</strong> e il Codice Deontologico</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={guestForm.accetto_privacy} onChange={(e) => setGuestForm({ ...guestForm, accetto_privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#102a6b]" data-testid="guest-privacy" />
                    <span className="text-[12.5px] text-slate-700">Accetto il trattamento dei dati personali ai sensi del <strong>GDPR (Reg. UE 2016/679)</strong></span>
                  </label>
                  {guestError && <div className="text-red-600 text-[12.5px] font-semibold">{guestError}</div>}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setGuestStep(1)} className="flex-1 h-11">← Indietro</Button>
                    <Button
                      onClick={submitGuest}
                      disabled={!guestForm.accetto_statuto || !guestForm.accetto_privacy || guestLoading}
                      className="flex-1 h-11 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0a1020] font-bold disabled:opacity-50"
                      data-testid="guest-submit"
                    >
                      {guestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Crea account <CheckCircle2 className="h-4 w-4 ml-1" /></>}
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 3 — Successo */}
              {guestStep === 3 && (
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <div>
                    <h4 className="font-extrabold text-[#0a1f55] text-xl">Account creato con successo!</h4>
                    <p className="text-[13.5px] text-slate-600 mt-2">
                      Le credenziali sono state inviate a <strong>{guestForm.email}</strong>.
                    </p>
                  </div>
                  <a
                    href={`${API}/api/pdf/guida-tutela-online`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#fef3c7] border-2 border-[#f59e0b] text-[#0a1f55] font-bold text-[14px] hover:bg-[#fbbf24]/40 transition-colors"
                    data-testid="guest-download-pdf"
                  >
                    <Gift className="h-5 w-5 text-[#f59e0b]" />
                    Scarica la guida regalo (PDF)
                    <FileText className="h-4 w-4" />
                  </a>
                  <div className="pt-2">
                    <Button
                      onClick={() => navigate("/dashboard")}
                      className="w-full h-11 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
                      data-testid="guest-go-dashboard"
                    >
                      Vai alla mia dashboard <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                  <p className="text-[11.5px] text-slate-500">
                    Per attivare la <strong>tessera ACDT (€ 50)</strong> paga ora con PayPal:{" "}
                    <a href="/#pacchetti" className="text-[#102a6b] underline font-bold">paga la tessera €50 →</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
